import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { DEMO } from '../../lib/demo';
import { getCached, setCached } from '../../lib/pageCache';
import { Search, Send, Mail, Megaphone } from 'lucide-react';

// Section Spenders / Relance — liste des meilleurs payeurs triés par modèle, ancienneté et
// montant, avec leurs contacts (Telegram + mail) pour les relancer même si on perd la conv.
// Données démo ; en prod = branché sur la base Airtable (historique fans / LTV) de Marlon.

const MODELS = ['Tous les modèles', 'Maya', 'Nova', 'Lia'];
const SORTS = [['spent', 'Montant dépensé'], ['tenure', 'Ancienneté'], ['last', 'Dernière vente']];
const MEDAL = ['#f5c451', '#cdd3e0', '#cd8b5a'];

// [name, model, totalSpent, lastSaleDays, fanSinceDays, telegram, email, tag]
const SPENDERS = [
  ['Yann B.', 'Maya', 1840, 2, 420, '@yann_b', 'yann.b@gmail.com', 'Baleine'],
  ['Alex R.', 'Maya', 1320, 12, 380, '@alex_r', 'alex.r@outlook.com', 'Baleine'],
  ['Marc P.', 'Nova', 980, 1, 210, '@marc_p', 'marc.p@gmail.com', 'Spender'],
  ['Théo L.', 'Maya', 760, 5, 150, '@theo_l', 'theo.l@gmail.com', 'Spender'],
  ['Renaud', 'Nova', 540, 8, 95, '@renaud', 'renaud@yahoo.fr', 'Spender'],
  ['Hugo M.', 'Lia', 480, 30, 60, '@hugo_m', 'hugo.m@gmail.com', 'À relancer'],
  ['Lucas D.', 'Nova', 320, 45, 240, '@lucas_d', 'lucas.d@gmail.com', 'À relancer'],
  ['Julien F.', 'Maya', 280, 3, 320, '@julien_f', 'julien.f@gmail.com', 'Fidèle'],
  ['Sofiane', 'Lia', 220, 60, 110, '@sofiane', 'sofiane@gmail.com', 'À relancer'],
];

const euro = (n) => n.toLocaleString('fr-FR') + ' €';
const tenure = (d) => (d >= 365 ? `${Math.floor(d / 365)} an${d >= 730 ? 's' : ''}${d % 365 >= 30 ? ' ' + Math.floor((d % 365) / 30) + ' mois' : ''}` : d >= 30 ? `${Math.floor(d / 30)} mois` : `${d} j`);
const ago = (d) => (d === 0 ? "aujourd'hui" : d === 1 ? 'hier' : d >= 30 ? `il y a ${Math.floor(d / 30)} mois` : `il y a ${d} j`);
const TAG_COLOR = { Baleine: '#37d39a', Spender: '#3f7dff', 'À relancer': '#e8a13a', Fidèle: '#8b91a8' };

export default function Spenders() {
  const navigate = useNavigate();
  const [model, setModel] = useState(0);
  const [sort, setSort] = useState('spent');
  const [q, setQ] = useState('');
  const [live, setLive] = useState(() => getCached('spenders-rows') || null);

  // En PROD : vraies données depuis l'API (fans payeurs, contacts, dates). En démo : statique.
  // AUTO-SYNC : on rafraîchit toutes les 30 s + au retour sur l'onglet → la liste reflète
  // toujours les derniers fans / paiements / modèles (fini les données figées).
  useEffect(() => {
    if (DEMO) return undefined;
    const daysSince = (iso) => { if (!iso) return 9999; const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000); return d < 0 ? 0 : d; };
    const tagFor = (spent, lastDays, lists) => {
      if (lists && lists.includes('À relancer')) return 'À relancer';
      if (spent >= 500) return 'Baleine';
      if (lastDays >= 21) return 'À relancer';
      if (spent >= 200) return 'Spender';
      return 'Fidèle';
    };
    let alive = true;
    const fetchSpenders = () => api.get('/reports/spenders').then((r) => {
      if (!alive) return;
      const mapped = (r.data || []).map((s) => {
        // s[3] = jours depuis la DERNIÈRE VENTE réelle (colonne « Dernière vente »).
        // Le tag « À relancer » se base sur l'INACTIVITÉ (dernier message), pas la vente.
        const saleDays = s.last_sale ? daysSince(s.last_sale) : null;
        const inactiveDays = daysSince(s.last_activity);
        return [s.name, (s.model || '—').trim(), s.spent || 0, saleDays, daysSince(s.fan_since),
          s.telegram ? '@' + String(s.telegram).replace(/^@/, '') : '—', s.email || '', tagFor(s.spent || 0, inactiveDays, s.lists)];
      });
      setLive(mapped);
      setCached('spenders-rows', mapped);
    }).catch(() => { if (alive) setLive((prev) => prev || []); });
    fetchSpenders();
    const iv = setInterval(fetchSpenders, 30000);
    const onFocus = () => fetchSpenders();
    window.addEventListener('focus', onFocus);
    return () => { alive = false; clearInterval(iv); window.removeEventListener('focus', onFocus); };
  }, []);

  const SRC = DEMO ? SPENDERS : (live || []);
  const MODELS_DYN = ['Tous les modèles', ...Array.from(new Set(SRC.map((s) => s[1]).filter((m) => m && m !== '—')))];
  const mSel = MODELS_DYN[model] || 'Tous les modèles';

  // Filtré par MODÈLE sélectionné (sert aussi aux compteurs du haut → ils suivent le tri).
  const inModel = SRC.filter((s) => model === 0 || s[1] === mSel);
  let rows = inModel.filter((s) => (!q || (s[0] || '').toLowerCase().includes(q.toLowerCase())));
  rows = [...rows].sort((a, b) => (sort === 'spent' ? b[2] - a[2] : sort === 'tenure' ? b[4] - a[4] : (a[3] ?? 1e9) - (b[3] ?? 1e9)));

  const whales = inModel.filter((s) => s[2] >= 500).length;
  const totalCA = inModel.reduce((s, x) => s + x[2], 0);
  const toRelance = inModel.filter((s) => s[7] === 'À relancer').length;

  return (
    <div className="p-6 max-w-[1440px] mx-auto" data-testid="spenders-page">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-white">Spenders &amp; relance</h1>
          <p className="text-sm text-slate-400 mt-1">Tes meilleurs payeurs, triés par modèle / ancienneté / montant — avec leurs contacts pour les relancer en priorité.</p>
        </div>
        <button onClick={() => navigate('/dashboard/mass-messages')} data-testid="launch-relance"
          className="shrink-0 inline-flex items-center gap-2 text-sm font-semibold text-white px-4 py-2.5 rounded-xl shadow-lg hover:brightness-110 transition" style={{ background: 'linear-gradient(135deg,#3f7dff,#73a6ff)' }}>
          <Megaphone size={16} /> Lancer une relance
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        {[['Baleines (≥ 500 €)', whales, '#37d39a'], ['CA cumulé spenders', euro(totalCA), '#3f7dff'], ['À relancer', toRelance, '#e8a13a']].map((k) => (
          <div key={k[0]} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <div className="text-xs text-slate-400">{k[0]}</div>
            <div className="text-3xl font-bold mt-1" style={{ color: k[2] }}>{k[1]}</div>
          </div>
        ))}
      </div>

      {/* filtres */}
      <div className="flex flex-wrap gap-2.5 items-center mb-4">
        {MODELS_DYN.map((m, i) => (
          <button key={m} onClick={() => setModel(i)}
            className={`px-3.5 h-9 rounded-full text-[13px] font-medium transition-colors ${i === model ? 'bg-indigo-500/20 text-white border border-indigo-500/40' : 'text-slate-400 hover:text-white border border-white/10'}`}>{m}</button>
        ))}
        <span className="flex-1" />
        <div className="flex gap-1 p-1 rounded-lg bg-white/5 border border-white/10">
          {SORTS.map(([k, l]) => (
            <button key={k} onClick={() => setSort(k)} className={`px-3 h-7 rounded-md text-xs font-medium transition-colors ${sort === k ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'}`}>{l}</button>
          ))}
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un fan…" className="bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 h-9 text-sm text-white outline-none w-52 placeholder:text-slate-600" />
        </div>
      </div>

      {/* tableau */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[920px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-white/5">
                <th className="text-left font-semibold px-4 py-3">#</th>
                <th className="text-left font-semibold px-4 py-3">Fan</th>
                <th className="text-left font-semibold px-4 py-3">Modèle</th>
                <th className="text-right font-semibold px-4 py-3">Total dépensé</th>
                <th className="text-left font-semibold px-4 py-3">Dernière vente</th>
                <th className="text-left font-semibold px-4 py-3">Fan depuis</th>
                <th className="text-left font-semibold px-4 py-3">Contacts</th>
                <th className="text-right font-semibold px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s, i) => (
                <tr key={`${s[0]}|${s[1]}|${i}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                  <td className="px-4 py-3">
                    {i < 3 ? <span className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold text-black" style={{ background: MEDAL[i] }}>{i + 1}</span> : <span className="text-slate-500">{i + 1}</span>}
                  </td>
                  <td className="px-4 py-3 font-medium text-white">{s[0]}</td>
                  <td className="px-4 py-3"><span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300">{s[1]}</span></td>
                  <td className="px-4 py-3 text-right font-bold text-emerald-300">{euro(s[2])}</td>
                  <td className="px-4 py-3 text-slate-400">{s[3] == null ? '—' : ago(s[3])}</td>
                  <td className="px-4 py-3 text-slate-300">{tenure(s[4])}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-0.5 text-[12px]">
                      <a className="text-indigo-300 hover:underline" href={`https://t.me/${s[5].replace('@', '')}`} target="_blank" rel="noreferrer">{s[5]}</a>
                      <span className="text-slate-500 flex items-center gap-1"><Mail size={11} /> {s[6]}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-[10px] px-2 py-0.5 rounded-full mr-2" style={{ background: (TAG_COLOR[s[7]] || '#8b91a8') + '22', color: TAG_COLOR[s[7]] || '#8b91a8' }}>{s[7]}</span>
                    <button onClick={() => navigate('/dashboard/messages')} title={`Ouvrir la conversation de ${s[0]}`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-white px-3 py-1.5 rounded-lg hover:brightness-110 transition" style={{ background: 'linear-gradient(135deg,#3f7dff,#73a6ff)' }}>
                      <Send size={12} /> Relancer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-[11px] text-slate-600 mt-3">Démo — en production cette liste se construit automatiquement depuis tes ventes DropFans (dépense, dernière vente, contact Telegram par fan). « Relancer » ouvre la conversation du fan ; « Lancer une relance » crée une campagne Mass DM sur les fans filtrés.</p>
    </div>
  );
}
